import React, { useState } from 'react';
import { Avatar, Tag } from 'antd';
import {
  Eye,
  Clock,
  LogOut,
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  X,
  BarChart3,
  Calendar,
  CheckSquare,
  Phone,
  ArrowRight,
} from 'lucide-react';

// Enhanced action metadata with Lucide icons and semantic colors
const actionMetadata = {
  view: { color: '#1890ff', bg: '#e6f4ff', icon: Eye, label: 'Viewed' },
  clockin: { color: '#52c41a', bg: '#f6ffed', icon: Clock, label: 'Clocked In' },
  clockout: { color: '#fa8c16', bg: '#fff7e6', icon: LogOut, label: 'Clocked Out' },
  add: { color: '#52c41a', bg: '#f6ffed', icon: Plus, label: 'Added' },
  update: { color: '#faad14', bg: '#fffbe6', icon: RefreshCw, label: 'Updated' },
  edit: { color: '#faad14', bg: '#fffbe6', icon: Edit, label: 'Edited' },
  delete: { color: '#ff4d4f', bg: '#fff1f0', icon: Trash2, label: 'Deleted' },
  cancel: { color: '#ff4d4f', bg: '#fff1f0', icon: X, label: 'Cancelled' },
  report: { color: '#722ed1', bg: '#f9f0ff', icon: BarChart3, label: 'Reported' },
  schedule: { color: '#13c2c2', bg: '#e6fffb', icon: Calendar, label: 'Scheduled' },
  task: { color: '#eb2f96', bg: '#fff0f6', icon: CheckSquare, label: 'Task' },
  call: { color: '#a0d911', bg: '#fcffe6', icon: Phone, label: 'Called' },
};

// Falls back to a short JSON snippet for an object value that isn't one of the
// common "named record" shapes below (e.g. a report-template field placement or a
// {dependsOnInstanceId, equals} condition) - previously rendered as '' → "(empty)",
// which made this diff view useless for any entity storing structured JSON fields.
const toString = (val) => {
  if (val == null) return '';
  if (typeof val !== 'object') return String(val);
  const named = val?.description ?? val?.name ?? val?.label;
  if (named != null) return String(named);
  try {
    const json = JSON.stringify(val);
    return json?.length > 80 ? `${json.slice(0, 77)}...` : json;
  } catch {
    return '';
  }
};

const isImageVal = (val) =>
  typeof val === 'string' && (val.startsWith('data:image') || /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(val));

const renderVal = (val) => {
  if (val == null) return '(empty)';
  const str = toString(val);
  if (isImageVal(str)) return <img src={str} alt="" style={{width:32, height:32, borderRadius:4, objectFit:'cover', verticalAlign:'middle'}} />;
  return str || '(empty)';
};

const ActivityItem = ({ user, action, target, location, time, change }) => {
  const [expanded, setExpanded] = useState(false);
  user = toString(user);
  target = toString(target);
  location = toString(location);
  action = toString(action);
  const actionKey = action?.toLowerCase() || 'view';
  const metadata = actionMetadata[actionKey] || {
    color: '#8c8c8c',
    bg: '#f5f5f5',
    icon: Eye,
    label: action,
  };

  const hasChanges = change && Object.keys(change).length > 0;
  const timeAgo = getTimeAgo(time);
  const IconComponent = metadata.icon;
  
  // Check if this is an update action
  const isUpdateAction = ['update', 'edit'].includes(actionKey);

  return (
    <div
      style={{
        display: 'flex',
        gap: '16px',
        padding: '16px 0',
        borderBottom: '1px solid #f0f0f0',
        transition: 'all 0.2s ease',
        cursor: hasChanges ? 'pointer' : 'default',
      }}
      onClick={() => hasChanges && setExpanded(!expanded)}
      onMouseEnter={(e) => {
        if (hasChanges) e.currentTarget.style.backgroundColor = '#fafafa';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {/* Left: Timeline indicator */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: metadata.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${metadata.color}20`,
          }}
        >
          <IconComponent size={20} color={metadata.color} strokeWidth={2} />
        </div>
        <div
          style={{
            width: '2px',
            height: '100%',
            backgroundColor: '#f0f0f0',
            marginTop: '4px',
          }}
        />
      </div>

      {/* Right: Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header Row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
          <Avatar
            size={24}
            style={{
              backgroundColor: metadata.color,
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {user?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#262626',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            {user}
          </span>
          <Tag
            color={metadata.color}
            style={{
              margin: 0,
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              padding: '2px 8px',
              border: 'none',
            }}
          >
            {metadata.label}
          </Tag>
          <span style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: 'auto' }}>
            {timeAgo}
          </span>
        </div>

        {/* Main Content */}
        <div style={{ marginLeft: '32px' }}>
          <div style={{ fontSize: '14px', color: '#595959', lineHeight: '1.6', marginBottom: '4px' }}>
            <span style={{ fontWeight: 500, color: '#262626' }}>{target}</span>
            {location && (
              <span>
                {' '}
                <span style={{ color: '#bfbfbf' }}>on</span>{' '}
                <span
                  style={{
                    color: '#1890ff',
                    fontWeight: 500,
                    padding: '2px 6px',
                    backgroundColor: '#f0f5ff',
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}
                >
                  {location}
                </span>
              </span>
            )}
          </div>

          {/* Inline Change Summary for Update Actions */}
          {isUpdateAction && hasChanges && !expanded && (
            <div style={{ marginTop: '8px', marginBottom: '4px' }}>
              {Object.entries(change).slice(0, 2).map(([field, values], idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    marginBottom: '4px',
                    color: '#595959',
                  }}
                >
                  <span style={{ fontWeight: 500, color: '#8c8c8c', fontSize: '12px' }}>
                    {field}:
                  </span>
                  <span
                    style={{
                      color: '#ff4d4f',
                      backgroundColor: '#fff1f0',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '12px',
                    }}
                  >
                    {renderVal(values.from)}
                  </span>
                  <ArrowRight size={12} color="#bfbfbf" />
                  <span
                    style={{
                      color: '#52c41a',
                      backgroundColor: '#f6ffed',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {renderVal(values.to)}
                  </span>
                </div>
              ))}
              {Object.keys(change).length > 2 && (
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                  +{Object.keys(change).length - 2} more change{Object.keys(change).length - 2 > 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {/* Timestamp */}
          {time && (
            <div style={{ fontSize: '12px', color: '#bfbfbf', marginTop: isUpdateAction && hasChanges && !expanded ? '4px' : '0', marginBottom: hasChanges ? '8px' : '0' }}>
              {new Date(time).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          )}

          {/* Changes Section (Expandable) */}
          {hasChanges && (
            <div style={{ marginTop: '8px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  color: '#8c8c8c',
                  fontWeight: 500,
                  marginBottom: '6px',
                }}
              >
                <span>{expanded ? '▼' : '▶'}</span>
                <span>{expanded ? 'Hide' : 'Show'} all changes ({Object.keys(change).length})</span>
              </div>
              {expanded && (
                <div
                  style={{
                    backgroundColor: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: '6px',
                    padding: '12px',
                    marginTop: '6px',
                    animation: 'slideDown 0.2s ease',
                  }}
                >
                  {Object.entries(change).map(([field, values], idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: idx < Object.keys(change).length - 1 ? '12px' : '0',
                        paddingBottom: idx < Object.keys(change).length - 1 ? '12px' : '0',
                        borderBottom:
                          idx < Object.keys(change).length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#8c8c8c',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '4px',
                          fontWeight: 600,
                        }}
                      >
                        {field}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <span
                          style={{
                            color: '#ff4d4f',
                            textDecoration: 'line-through',
                            backgroundColor: '#fff1f0',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            flex: 1,
                          }}
                        >
                          {renderVal(values.from)}
                        </span>
                        <span style={{ color: '#bfbfbf' }}>→</span>
                        <span
                          style={{
                            color: '#52c41a',
                            backgroundColor: '#f6ffed',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 500,
                            flex: 1,
                          }}
                        >
                          {renderVal(values.to)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// Helper function to calculate relative time
function getTimeAgo(timestamp) {
  if (!timestamp) return '';
  
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default ActivityItem;